import React from 'react';

/**
 * PaceIndicator — answers "is the current sales rate matching target?"
 *
 * Formula (as specified by Dipali):
 *   expected = (days_elapsed / days_in_period) × target
 *   pace_%   = actual / expected × 100
 *
 * Colour/status:
 *   >=100  → Ahead of pace   (green, rocket)
 *   85-100 → On pace         (green, check)
 *   70-85  → Slightly behind (amber, warning)
 *   <70    → Behind pace     (red, alert)
 *
 * Props:
 *   actual        number — revenue realised in the period
 *   target        number — target for the full period (quarter / year)
 *   daysElapsed   number — days that have passed within the period
 *   daysInPeriod  number — total days in the period
 *   size          'sm' | 'md' — optional, default 'md'
 *   showPct       boolean — also show "75%" label (default true)
 *   showStatus    boolean — also show "On pace" text (default true)
 */
export default function PaceIndicator({
  actual = 0,
  target = 0,
  daysElapsed = 0,
  daysInPeriod = 91,
  size = 'md',
  showPct = true,
  showStatus = true,
  invoicedLabel = false,
}) {
  if (!target || !daysInPeriod) return null;
  const expected = (daysElapsed / daysInPeriod) * target;
  if (expected <= 0) return null;

  const pacePct = Math.round((actual / expected) * 100);

  let status, colour, bg, border, icon;
  if (pacePct >= 100) {
    status = 'Ahead';
    colour = '#14532D';
    bg = '#DCFCE7';
    border = '#86EFAC';
    icon = '▲';
  } else if (pacePct >= 85) {
    status = 'On pace';
    colour = '#166534';
    bg = '#ECFDF5';
    border = '#BBF7D0';
    icon = '✓';
  } else if (pacePct >= 70) {
    status = 'Slightly behind';
    colour = '#854F0B';
    bg = '#FFFBEB';
    border = '#FDE68A';
    icon = '!';
  } else {
    status = 'Behind';
    colour = '#991B1B';
    bg = '#FEF2F2';
    border = '#FCA5A5';
    icon = '▼';
  }

  const sizeCls = size === 'sm'
    ? 'text-[9px] px-1.5 py-0.5 gap-1'
    : 'text-[10px] px-2 py-0.5 gap-1';

  const label = [];
  if (showPct) label.push(invoicedLabel ? `${pacePct}% invoiced` : `${pacePct}%`);
  if (showStatus && !invoicedLabel) label.push(status);
  if (showStatus && invoicedLabel) label.push(pacePct === 0 ? 'Not yet billed' : status);

  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold tabular-nums ${sizeCls}`}
      style={{ backgroundColor: bg, color: colour, border: `1px solid ${border}` }}
      title={`Actual ${actual.toLocaleString('en-IN')} vs expected ${Math.round(expected).toLocaleString('en-IN')} by day ${daysElapsed} of ${daysInPeriod}`}
      data-testid="pace-indicator"
      data-pace-status={status.toLowerCase().replace(' ', '-')}
    >
      <span aria-hidden>{icon}</span>
      {label.join(' · ')}
    </span>
  );
}

/**
 * Utility: compute days elapsed + days-in-period for "annual" (FY Apr-Mar) or "quarterly" (FY calendar Q1-Q4).
 * Used for CEO dashboard cards that only have annual targets.
 */
export function annualFYDays(nowDate = new Date()) {
  const y = nowDate.getMonth() >= 3 ? nowDate.getFullYear() : nowDate.getFullYear() - 1;
  const start = new Date(y, 3, 1); // April 1
  const daysElapsed = Math.max(1, Math.floor((nowDate - start) / 86400000) + 1);
  return { daysElapsed: Math.min(daysElapsed, 365), daysInPeriod: 365 };
}
